import { View, YStack } from 'tamagui';

import { gray } from '../../themes/colors/palettes/gray';
import { Shadows } from '../Shadows';
import {
    ALT_COLORS,
    ALT_SECONDARY_COLORS,
    DEFAULT_COLOR,
    DEFAULT_ICON,
    FRUITFUL_ICON_KEY,
    SPACE_COLORS,
    SPACE_ICONS,
    SPACE_SECONDARY_COLORS,
} from './FruitfulCustomIconDefaults';
import { Icon, type IconType } from './Icon';

export type FruitfulCustomIconSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export const FruitfulCustomIconSizeMap: Record<FruitfulCustomIconSize, number> =
    {
        xxs: 24,
        xs: 32,
        sm: 40,
        md: 48,
        lg: 70,
        xl: 96,
    };

const ICON_SIZE_MAP: Record<FruitfulCustomIconSize, number> = {
    xxs: 14,
    xs: 16,
    sm: 20,
    md: 24,
    lg: 36,
    xl: 44,
};

const getPaddingSize = (size: FruitfulCustomIconSize) => {
    return (FruitfulCustomIconSizeMap[size] - ICON_SIZE_MAP[size]) / 2;
};

const PADDING_SIZE_MAP: Record<FruitfulCustomIconSize, number> = {
    xxs: getPaddingSize('xxs'),
    xs: getPaddingSize('xs'),
    sm: getPaddingSize('sm'),
    md: getPaddingSize('md'),
    lg: getPaddingSize('lg'),
    xl: getPaddingSize('xl'),
};

export const FruitfulCustomIconBorderRadiusMap: Record<
    FruitfulCustomIconSize,
    number
> = {
    xxs: 8,
    xs: 10,
    sm: 12,
    md: 15,
    lg: 22,
    xl: 28,
};

export const FruitfulCustomIconShadow = Shadows.MEDIUM;

interface FruitfulCustomIconProps {
    icon?: string | null;
    color?: string | null;
    size?: FruitfulCustomIconSize;
    customSize?: { iconSize: number; padding: number };
    showShadow?: boolean;
    useAltColors?: boolean;
    muted?: boolean;
}

const getIconColor = ({
    muted,
    useAltColors,
    color,
    defaultColorKey,
}: {
    muted: boolean;
    useAltColors: boolean;
    color: string | null | undefined;
    defaultColorKey: string;
}): string => {
    if (muted) {
        return gray.gray800;
    }

    if (useAltColors) {
        return (
            ALT_SECONDARY_COLORS[color ?? ''] ??
            ALT_SECONDARY_COLORS[defaultColorKey]
        );
    }

    return (
        SPACE_SECONDARY_COLORS[color ?? ''] ??
        SPACE_SECONDARY_COLORS[defaultColorKey]
    );
};

export const FruitfulCustomIcon = ({
    icon,
    color,
    size = 'md',
    customSize,
    showShadow,
    useAltColors = false,
    muted = false,
}: FruitfulCustomIconProps) => {
    const defaultColorKey = useAltColors ? 'peach' : DEFAULT_COLOR;
    const defaultIconKey = useAltColors ? FRUITFUL_ICON_KEY : DEFAULT_ICON;

    const backgroundColor = useAltColors
        ? (ALT_COLORS[color ?? ''] ?? ALT_COLORS[defaultColorKey])
        : (SPACE_COLORS[color ?? ''] ?? SPACE_COLORS[defaultColorKey]);

    const iconColor = getIconColor({
        muted,
        useAltColors,
        color,
        defaultColorKey,
    });

    const iconDefinition =
        SPACE_ICONS[icon ?? ''] ?? SPACE_ICONS[defaultIconKey];

    return (
        <YStack alignItems="center" justifyContent="center">
            <View
                backgroundColor={backgroundColor}
                borderRadius={FruitfulCustomIconBorderRadiusMap[size]}
                padding={customSize?.padding ?? PADDING_SIZE_MAP[size] - 1} // -1 to account for border width
                alignItems="center"
                style={showShadow ? { ...FruitfulCustomIconShadow } : {}}
                borderWidth={1}
                borderColor={useAltColors ? '$gray400' : backgroundColor}
            >
                <Icon
                    icon={iconDefinition as IconType}
                    stringColor={iconColor}
                    size={customSize?.iconSize ?? ICON_SIZE_MAP[size]}
                />
            </View>
        </YStack>
    );
};
